import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { PolicyLayout, PolicySection } from "@/components/PolicyLayout";

export default function AntiSpamPolicy() {
  return (
    <>
      <Seo
        title="Anti-Spam & Unsolicited Advertising Policy — Symmori"
        description="Symmori's public position on responsible business communications, the prohibition of spam and deceptive promotion, and how to report suspected abuse of its name or domains."
        path="/anti-spam-policy"
      />
      <PolicyLayout
        overline="Legal"
        title="Anti-Spam & Unsolicited Advertising Policy"
        updated="July 2026"
        testid="anti-spam-policy-page"
      >
        <PolicySection title="Our Commitment" testid="antispam-commitment">
          <p>
            Symmori is committed to responsible, relevant and transparent business
            communications. We do not authorise the use of our name, website or
            domains for spam, deceptive messages or abusive promotional activity.
          </p>
        </PolicySection>

        <PolicySection title="Permitted Business Communications" testid="antispam-permitted">
          <p>
            Symmori is an influencer marketing agency. As a normal part of our
            business, we may contact YouTube creators and their representatives about
            relevant, potential paid sponsorship opportunities on behalf of brands.
          </p>
          <p>Any business communication we send must:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Be relevant to the recipient's content, role or business;</li>
            <li>Accurately identify the sender and Symmori;</li>
            <li>Describe the purpose of the message honestly;</li>
            <li>Respect applicable laws and the recipient's stated preferences, including any request not to be contacted again.</li>
          </ul>
          <p>
            We do not represent that every business introduction we send is solicited
            or that all recipients have opted in; we do commit to the standards above
            for every message we send.
          </p>
        </PolicySection>

        <PolicySection title="Prohibition on Abusive Promotion" testid="antispam-prohibition">
          <p>Symmori prohibits:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Unauthorised use of its website or domains in spam campaigns;</li>
            <li>Deceptive or misleading messages promoting Symmori;</li>
            <li>Unsolicited bulk messaging conducted in violation of applicable law;</li>
            <li>Use of false identities, misleading subject lines or fraudulent representations;</li>
            <li>Abuse of Symmori's name by unauthorised third parties.</li>
          </ul>
        </PolicySection>

        <PolicySection title="Affiliates and Third Parties" testid="antispam-affiliates">
          <p>
            Any affiliates, contractors, partners or other third parties authorised to
            represent Symmori must comply with this policy. We will investigate
            credible reports of misuse and take appropriate action within our control,
            including ending an offending relationship where appropriate.
          </p>
        </PolicySection>

        <PolicySection title="Reporting Suspected Abuse" testid="antispam-reporting">
          <p>
            If you have received a message that appears to be spam, impersonation, or
            an unauthorised use of Symmori's name or domains, please report it to us
            at{" "}
            <a
              href="mailto:admin@symmori.com?subject=Abuse%20Report"
              className="underline underline-offset-4 decoration-ink/30 hover:decoration-mutedblue"
              data-testid="antispam-report-email"
            >
              admin@symmori.com
            </a>{" "}
            with the subject line "Abuse Report", including the full message and any
            headers or evidence you can provide. You can also use our{" "}
            <Link
              to="/contact"
              className="underline underline-offset-4 decoration-ink/30 hover:decoration-mutedblue"
              data-testid="antispam-report-form-link"
            >
              contact form
            </Link>{" "}
            and select "Report abuse or impersonation".
          </p>
          <p>
            We review every credible report we receive and act on those within our
            control.
          </p>
        </PolicySection>

        <PolicySection title="Compliance and Review" testid="antispam-compliance">
          <p>
            Symmori periodically reviews its communication practices and takes
            reasonable steps to address misuse of its identity and domains. This
            policy is effective from July 2026 and may be updated from time to time;
            material changes will be reflected on this page.
          </p>
          <p>
            Questions about this policy can be sent to{" "}
            <a
              href="mailto:admin@symmori.com"
              className="underline underline-offset-4 decoration-ink/30 hover:decoration-mutedblue"
            >
              admin@symmori.com
            </a>
            .
          </p>
        </PolicySection>
      </PolicyLayout>
    </>
  );
}
